import * as React from 'react';
import { KeyCodes } from '@fluentui/react/lib/Utilities';
import type { AssetId } from 'owa-timestream-types';
import { setAnnouncement, getAsset } from 'owa-timestream-store';
import {
    useProjectId,
    getAssetDescriptor,
    GRID_SIZE,
    isTextInputElement,
} from 'owa-timestream-asset-sdk';
import { focusAsset } from 'owa-timestream-asset-sdk-actions';
import {
    getNearbyPointsForAsset,
    NearbyAssets,
    getSelectedAssets,
    moveAssetArrow,
} from '../viewstate';
import loc from 'owa-localize';
import {
    noAssetUpAnnouncement,
    noAssetDownAnnouncement,
    noAssetLeftAnnouncement,
    noAssetRightAnnouncement,
} from './CanvasFocusZone.locstring.json';
import {
    multipleLeftMove,
    multipleRightMove,
    multipleUpMove,
    multipleDownMove,
    leftMove,
    rightMove,
    upMove,
    downMove,
} from '../components/assetPositioningAriaLabel.locstring.json';

const KEYDOWN_ASSET_INFO = {
    [KeyCodes.left]: [-GRID_SIZE, 0, leftMove, multipleLeftMove, 'left', noAssetLeftAnnouncement],
    [KeyCodes.right]: [
        GRID_SIZE,
        0,
        rightMove,
        multipleRightMove,
        'right',
        noAssetRightAnnouncement,
    ],
    [KeyCodes.up]: [0, -GRID_SIZE, upMove, multipleUpMove, 'up', noAssetUpAnnouncement],
    [KeyCodes.down]: [0, GRID_SIZE, downMove, multipleDownMove, 'down', noAssetDownAnnouncement],
};

export default function useTimeStreamDocumentSurfaceViewState() {
    const projectId = useProjectId();
    const onKeyDown = React.useCallback(
        (e: React.KeyboardEvent<HTMLDivElement>) => {
            if (e.which in KEYDOWN_ASSET_INFO) {
                const [
                    x,
                    y,
                    moveDirectionAnnouncement,
                    multipleMoveDirectionAnnouncement,
                    directionName,
                    noAsset,
                ] = KEYDOWN_ASSET_INFO[e.which];
                if (
                    (e.ctrlKey || e.metaKey) &&
                    e.shiftKey &&
                    !isTextInputElement(e.target as HTMLInputElement)
                ) {
                    const selectedAssets = getSelectedAssets();
                    const assetDesc = getAssetDescriptor(
                        getAsset(projectId, selectedAssets[0]).type
                    );

                    moveAssetArrow(x, y);
                    setAnnouncement(
                        selectedAssets.length > 1
                            ? loc(multipleMoveDirectionAnnouncement)
                            : loc(moveDirectionAnnouncement, assetDesc?.getToolName())
                    );
                    e.preventDefault();
                    e.stopPropagation();
                } else {
                    const currentFocusedAssetId = (e.target as HTMLDivElement).id as AssetId;
                    const nearbyPoints: NearbyAssets | undefined = getNearbyPointsForAsset(
                        currentFocusedAssetId
                    );
                    let nextFocusableAssetId = null as AssetId | null;
                    let noAssetAnnouncement = '';
                    if (nearbyPoints) {
                        nextFocusableAssetId = nearbyPoints[directionName];
                        noAssetAnnouncement = loc(noAsset);

                        if (nextFocusableAssetId) {
                            focusAsset(projectId, nextFocusableAssetId);
                        } else {
                            setAnnouncement(noAssetAnnouncement);
                        }
                    }
                }
            }
        },
        [projectId]
    );

    return {
        onKeyDown,
    };
}
